import { useQuery } from "@tanstack/react-query";
import { supabaseClient } from "../../supabase";

/**
 * How often a row still waiting on its extraction is re-read.
 *
 * The extraction runs as a background task on the ingest function and normally
 * answers in seconds, so 5 s is short enough that a curator watching the row
 * sees it land rather than reaching for reload, and long enough that a tab left
 * open on a stranded row costs twelve single-row reads a minute, not sixty.
 */
export const PENDING_POLL_INTERVAL_MS = 5000;

/**
 * The poll predicate, pulled out as a pure function so it can be asserted
 * directly: the page that renders a pending row mocks this whole hook, so
 * nothing else in the suite ever evaluates it.
 *
 * Poll ONLY while the row is pending, and stop the moment it is not. `ready`
 * and `failed` are both terminal — nothing else moves the row — and `null` is
 * every Jotform submission and every row written before import statuses
 * existed. A poll that never stops is a poll nobody notices is running.
 */
export const pendingRefetchInterval = (
  importStatus: unknown
): number | false =>
  importStatus === "pending" ? PENDING_POLL_INTERVAL_MS : false;

export const useQueryApplication = (id: number, enabled = true) => {
  const query = useQuery({
    queryKey: ["application", id],
    queryFn: async () => {
      if (!id) throw new Error("Application ID is required");

      const { data, error } = await supabaseClient
        .from("applications")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id && enabled,
    // Deliberately no `refetchIntervalInBackground`: a hidden tab does not need
    // to keep asking, and the hidden tab is the one that gets forgotten.
    refetchInterval: (currentQuery) =>
      pendingRefetchInterval(currentQuery.state.data?.import_status),
  });

  return {
    ...query,
    data: query.data,
    isLoading: query.isLoading,
  };
};
